import { inject } from '@angular/core';
import { CanActivateFn, Params, Router, UrlTree } from '@angular/router';
import { ENVIRONMENT } from '@lib-common/types/environment.types';

import { isSearchNgoTabEnabled } from '../search/search-ngo.feature';
import { parseTypeParam } from '../search/search-tab';

/** The legacy search page's tab fragments and the `type` each one means (`null`: All, which the page writes as no `type`). */
const LEGACY_TABS: ReadonlyMap<string, 'company' | 'offering' | null> = new Map([
  ['companies', 'company'],
  ['offerings', 'offering'],
  ['all', null],
]);

/**
 * Story 36.10b (AC 2, AC 3; ruling 47) — one guard for the search page's two addresses.
 *
 * `/ai-search`, the page's address until this story, is guard-only: it redirects to `/search`, every query key keeping all its
 * values in their order. On both routes the legacy page's tab fragment (`#companies`, `#offerings`, `#all`) becomes the page's
 * `type` and is consumed: a single valid explicit `type` (the page's own reading, `parseTypeParam`) wins; otherwise the URL that
 * lands carries the fragment's tab alone. Any other fragment is kept. `/search` without a legacy fragment activates as it is, so a
 * redirect's target never redirects again, and without one the guard never touches `type`.
 *
 * The returned `UrlTree` keeps the arriving navigation's replace decision and never forces one (36.8c PURPLE-1: a forced replace
 * overwrites the previous page's entry under the deferred URL update; a forced push leaves `/ai-search` under `/search`), so a
 * typed or followed legacy link leaves one history entry. The guard cleans no facet — 36.8c's clean-up stays the page's (ruling 47
 * ★2) — and the server-side 301 is Epic 55's.
 */
export const searchRouteGuard: CanActivateFn = (route): boolean | UrlTree => {
  const fragment = route.fragment;
  const tab = fragment === null ? undefined : LEGACY_TABS.get(fragment);
  const legacy = tab !== undefined;
  if (!legacy && route.routeConfig?.path !== 'ai-search') return true;
  const explicitWins = legacy && parseTypeParam(route.queryParamMap, isSearchNgoTabEnabled(inject(ENVIRONMENT))) !== 'all';
  const queryParams = legacy && !explicitWins ? withTab(route.queryParams, tab) : route.queryParams;
  return inject(Router).createUrlTree(['/search'], { queryParams, fragment: legacy || fragment === null ? undefined : fragment });
};

/** The query with its `type` set to the legacy tab, in place (appended when absent), or taken out for All. */
function withTab(params: Params, tab: 'company' | 'offering' | null): Params {
  const next: Params = {};
  for (const [key, value] of Object.entries(params)) {
    if (key !== 'type') next[key] = value;
    else if (tab !== null) next[key] = tab;
  }
  if (tab !== null && !('type' in next)) next['type'] = tab;
  return next;
}
